/** @jsxRuntime classic */
/*
This module was taken directly from @zapier/common-components StyleReset https://github.com/zapier/zapier/blob/8a883bf2305a8043d6e48234dffbe07c8efb2eea/packages/libraries/common-components/src/components/StylesReset/StylesReset.js#L46
and trimmed down to:
- Remove the concept of opt-in scoping of styles via @jsx isolateComponent
- Expose as a style declaration to be rendered via Global elsewhere
 */

const resetValue = 'unset';

const blockElements = [
  'address',
  'article',
  'aside',
  'blockquote',
  'details',
  'dialog',
  'dd',
  'div',
  'dl',
  'dt',
  'fieldset',
  'figcaption',
  'figure',
  'footer',
  'form',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'header',
  'hgroup',
  'hr',
  'li',
  'main',
  'nav',
  'ol',
  'p',
  'pre',
  'section',
  'table',
  'ul',
];

// Styles fallback for browsers that don't support `all` property.
const properties = [
  'alignContent',
  'alignItems',
  'alignSelf',
  'animationDelay',
  'animationDirection',
  'animationDuration',
  'animationFillMode',
  'animationIterationCount',
  'animationName',
  'animationPlayState',
  'animationTimingFunction',
  'appearance',
  'mozAppearance',
  'msAppearance',
  'webkitAppearance',
  'backfaceVisibility',
  'backgroundAttachment',
  'backgroundBlendMode',
  'backgroundClip',
  'backgroundColor',
  'backgroundImage',
  'backgroundOrigin',
  'backgroundPosition',
  'backgroundRepeat',
  'backgroundSize',
  'borderBottomColor',
  'borderBottomLeftRadius',
  'borderBottomRightRadius',
  'borderBottomStyle',
  'borderBottomWidth',
  'borderCollapse',
  'borderLeftColor',
  'borderLeftStyle',
  'borderLeftWidth',
  'borderRightColor',
  'borderRightStyle',
  'borderRightWidth',
  'borderSpacing',
  'borderTopColor',
  'borderTopLeftRadius',
  'borderTopRightRadius',
  'borderTopStyle',
  'borderTopWidth',
  'bottom',
  'boxShadow',
  'boxSizing',
  'clear',
  'clip',
  'clipPath',
  'color',
  'columnCount',
  'columnFill',
  'columnGap',
  'columnRuleColor',
  'columnRuleStyle',
  'columnRuleWidth',
  'columnSpan',
  'columnWidth',
  'content',
  'counterIncrement',
  'counterReset',
  'cursor',
  'display',
  'filter',
  'flexBasis',
  'flexDirection',
  'flexGrow',
  'flexShrink',
  'flexWrap',
  'float',
  'fontFamily',
  'fontFeatureSettings',
  'fontKerning',
  'fontLanguageOverride',
  'fontSize',
  'fontSizeAdjust',
  'fontStretch',
  'fontStyle',
  'fontVariant',
  'fontVariantAlternates',
  'fontVariantCaps',
  'fontVariantEastAsian',
  'fontVariantLigatures',
  'fontVariantNumeric',
  'fontVariantPosition',
  'fontWeight',
  'gridAutoColumns',
  'gridAutoFlow',
  'gridAutoRows',
  'gridColumnEnd',
  'gridColumnGap',
  'gridColumnStart',
  'gridRowEnd',
  'gridRowGap',
  'gridRowStart',
  'gridTemplateAreas',
  'gridTemplateColumns',
  'gridTemplateRows',
  'height',
  'hyphens',
  'inlineSize',
  'isolation',
  'justifyContent',
  'left',
  'letterSpacing',
  'lineBreak',
  'lineHeight',
  'listStyleImage',
  'listStylePosition',
  'listStyleType',
  'marginBottom',
  'marginLeft',
  'marginRight',
  'marginTop',
  'maxHeight',
  'maxWidth',
  'minHeight',
  'minWidth',
  'mixBlendMode',
  'objectFit',
  'opacity',
  'order',
  'orphans',
  'outlineColor',
  'outlineStyle',
  'outlineWidth',
  'overflow',
  'overflowWrap',
  'overflowX',
  'overflowY',
  'paddingBottom',
  'paddingLeft',
  'paddingRight',
  'paddingTop',
  'perspective',
  'perspectiveOrigin',
  'pointerEvents',
  'position',
  'quotes',
  'resize',
  'right',
  'scrollBehavior',
  'scrollSnapCoordinate',
  'scrollSnapDestination',
  'scrollSnapPointsX',
  'scrollSnapPointsY',
  'scrollSnapType',
  'textAlign',
  'textAlignLast',
  'textDecoration',
  'textIndent',
  'textShadow',
  'textTransform',
  'top',
  'transform',
  'transformOrigin',
  'transitionDelay',
  'transitionDuration',
  'transitionProperty',
  'transitionTimingFunction',
  'verticalAlign',
  'visibility',
  'whiteSpace',
  'width',
  'willChange',
  'wordBreak',
  'wordSpacing',
  'wordWrap',
  'zIndex',
];

// Sets all `properties` values to `resetValue`.
// Mutates `resetObject` to avoid creating a ton of instant
// garbage objects during the `reduce`.
const resetPropertyStyles = properties.reduce((resetObj: any, property) => {
  resetObj[property] = resetValue;
  return resetObj;
}, {});

export const resetStyles = {
  // Reset properties using `all` keyword when available.
  [`@supports (all: ${resetValue})`]: {
    all: resetValue,
  },
  // Reset properties one-by-one when `all` keyword is unavailable.
  [`@supports not (all: ${resetValue})`]: {
    ...resetPropertyStyles,
    // Address Edge bug where `display: unset` behaves like
    // `display: inherit` rather than `display: initial`.
    // The `blockElements` should handle other cases.
    // See https://bit.ly/2MDw3nx for details.
    display: 'initial',
  },
  // Address Safari bug where `color` doesn't inherit properly.
  // See https://bit.ly/2B7ofWm for details.
  '*&': {
    WebkitTextFillColor: 'currentColor',
  },
  // Elements `display: inline` by default depending on `resetValue`,
  // so ensure that block-level elements still display block. The qualifying
  // tag selector adds to specificity, but not enough to conflict with
  // component class selectors.
  [blockElements.map((el) => `${el}&`).join(', ')]: {
    display: 'block',
    margin: 0,
    paddingLeft: 0,
  },
  '*': { boxSizing: 'border-box' },
  '*:focus': {
    outline: 'none',
    border: 'none',
  },
  body: {
    margin: 0,
  },
  input: {
    border: 'none',
  },
  button: {
    border: 'none',
    padding: 0,
  },
  a: {
    textDecoration: 'none',
  },
  'a, label, button:not([disabled])': {
    cursor: 'pointer',
  },
  strong: {
    fontWeight: 'bold',
  },
  em: {
    fontStyle: 'italic',
  },
  // Fixes the `details-element-polyfill` breaking due to reset's specificity
  // winning out over the polyfill's styles
  'details:not([open]) > *:not(summary)': {
    display: 'none',
  },
};
